/**
 * Pure-function tree builder for Simple Mode.
 *
 * Takes the same {nodes, edges} that drive Canvas Mode and returns a
 * tree describing how to render the workflow as a vertical card list:
 *
 *   { kind: 'step',           node }
 *   { kind: 'branch',         source, branches: [{ label, sourceHandle, children }], converge }
 *   { kind: 'cycle-warning',  node }
 *
 * The top-level result is { roots: [{trigger, chain}], orphanIds }.
 *
 * Branch detection is currently scoped to `conditional` nodes (true/false).
 * Router and other multi-out node types fall through to a generic
 * "first outgoing edge wins" walk in this PR, with TODO for 2c.
 */

const TRIGGER_TYPES = new Set([
  'cron',
  'manual',
  'workflowTrigger',
  'webhook',
  'formSubmission',
  'recordChange',
]);

const BRANCHING_TYPES = new Set(['conditional', 'router']);
const LOOP_TYPES = new Set(['iterate']);

function nodeType(node) {
  return (node && node.data && node.data.nodeType) || '';
}

/**
 * Build adjacency maps once. Returns:
 *   nodesById: Map<id, node>
 *   outBy:     Map<sourceId, edge[]>
 *   inDegree:  Map<id, number>
 */
function buildIndex(nodes, edges) {
  const nodesById = new Map();
  for (const n of nodes) {
    if (n && n.id) nodesById.set(n.id, n);
  }

  const outBy = new Map();
  const inDegree = new Map();
  for (const id of nodesById.keys()) {
    outBy.set(id, []);
    inDegree.set(id, 0);
  }
  for (const e of edges) {
    if (!e || !nodesById.has(e.source) || !nodesById.has(e.target)) continue;
    outBy.get(e.source).push(e);
    inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1);
  }
  // Stable, human-friendly ordering for deterministic output. Conditional
  // 'true' renders before 'false'; 'default' (router fallthrough) renders last.
  const HANDLE_RANK = (h) => {
    if (h === 'true') return 0;
    if (h === 'false') return 1;
    if (h === 'default') return 99;
    return 50;
  };
  for (const list of outBy.values()) {
    list.sort((a, b) => {
      const ra = HANDLE_RANK(a.sourceHandle);
      const rb = HANDLE_RANK(b.sourceHandle);
      if (ra !== rb) return ra - rb;
      const ha = a.sourceHandle || '';
      const hb = b.sourceHandle || '';
      if (ha !== hb) return ha < hb ? -1 : 1;
      return a.target < b.target ? -1 : 1;
    });
  }
  return { nodesById, outBy, inDegree };
}

/**
 * Find roots: trigger-type nodes with no incoming edges. If none, fall
 * back to any node with no incoming edges (so a malformed workflow
 * still renders something).
 */
function findRoots(nodesById, inDegree) {
  const triggers = [];
  const orphanRoots = [];
  for (const node of nodesById.values()) {
    if ((inDegree.get(node.id) || 0) > 0) continue;
    if (TRIGGER_TYPES.has(nodeType(node))) {
      triggers.push(node);
    } else {
      orphanRoots.push(node);
    }
  }
  if (triggers.length > 0) return triggers;
  return orphanRoots;
}

/**
 * Compute the set of all node ids reachable from `startId` (forward).
 * Used by branch convergence: the converge node is the first node that
 * appears in *every* branch's reachable set in the order it appears in
 * the first branch.
 */
function reachableFrom(startId, outBy) {
  const visited = new Set();
  const stack = [startId];
  while (stack.length) {
    const id = stack.pop();
    if (visited.has(id)) continue;
    visited.add(id);
    for (const e of outBy.get(id) || []) {
      stack.push(e.target);
    }
  }
  return visited;
}

/**
 * Find the converge node for a set of branch starts: the first node
 * (in branchStarts[0]'s walk order) that is reachable from every branch.
 */
function findConvergeNode(branchStartIds, outBy) {
  if (branchStartIds.length < 2) return null;
  const reachableSets = branchStartIds.map((id) => reachableFrom(id, outBy));
  // Walk branch 0 in BFS order; first id present in all other reachables wins.
  const queue = [branchStartIds[0]];
  const seen = new Set();
  while (queue.length) {
    const id = queue.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    if (reachableSets.slice(1).every((s) => s.has(id))) {
      return id;
    }
    for (const e of outBy.get(id) || []) {
      queue.push(e.target);
    }
  }
  return null;
}

/**
 * Walk forward from `startId`, building a chain of layout nodes. Stops at:
 *   - end of chain (no outgoing edges)
 *   - revisiting a node already in the current path (emits cycle-warning, halts)
 *   - reaching `stopAtId` (used to terminate branch walks before the converge node)
 *   - reaching a node already visited globally (already rendered earlier)
 *
 * Branch handling: when we hit a node in BRANCHING_TYPES with multiple
 * outgoing edges, we emit a `branch` layout node with one child chain
 * per outgoing handle, and resume the parent chain at the converge node.
 */
function walkChain({
  startId,
  outBy,
  nodesById,
  nodeConfigs,
  visited,
  pathStack,
  stopAtId = null,
}) {
  const chain = [];
  let currentId = startId;

  while (currentId && currentId !== stopAtId) {
    if (pathStack.has(currentId)) {
      const node = nodesById.get(currentId);
      if (node) chain.push({ kind: 'cycle-warning', node });
      break;
    }
    if (visited.has(currentId)) break;

    const node = nodesById.get(currentId);
    if (!node) break;
    visited.add(currentId);
    pathStack.add(currentId);

    const outs = outBy.get(currentId) || [];
    const t = nodeType(node);
    const isBranch = BRANCHING_TYPES.has(t) && outs.length >= 2;
    const isLoop = LOOP_TYPES.has(t);

    if (isLoop) {
      // Iterate (for-each) renders as a wrapper card with an indented body.
      // Convention: the "body" handle is sourceHandle === 'body' (or the
      // first outgoing edge if no body handle exists). The "after" handle
      // is sourceHandle === 'after' (or the second/remaining edge).
      const bodyEdge =
        outs.find((e) => e.sourceHandle === 'body') || outs[0] || null;
      const afterEdge =
        outs.find((e) => e.sourceHandle === 'after') ||
        outs.find((e) => e !== bodyEdge) ||
        null;

      let bodyChildren = [];
      if (bodyEdge) {
        const bodyVisited = new Set();
        const bodyPath = new Set(pathStack);
        bodyChildren = walkChain({
          startId: bodyEdge.target,
          outBy,
          nodesById,
          nodeConfigs,
          visited: bodyVisited,
          pathStack: bodyPath,
        });
        for (const id of bodyVisited) visited.add(id);
      }

      chain.push({
        kind: 'loop',
        source: node,
        body: bodyChildren,
      });

      currentId = afterEdge ? afterEdge.target : null;
      continue;
    }

    if (isBranch) {
      const branchStartIds = outs.map((e) => e.target);
      const convergeId = findConvergeNode(branchStartIds, outBy);
      const sourceConfig = (nodeConfigs && nodeConfigs[node.id]) || null;

      const branches = outs.map((e) => {
        const branchVisited = new Set();
        const branchPath = new Set(pathStack);
        const children = walkChain({
          startId: e.target,
          outBy,
          nodesById,
          nodeConfigs,
          visited: branchVisited,
          pathStack: branchPath,
          stopAtId: convergeId,
        });
        for (const id of branchVisited) visited.add(id);
        return {
          label: humanLabelForHandle(e.sourceHandle, t, sourceConfig),
          sourceHandle: e.sourceHandle || null,
          targetId: e.target,
          children,
        };
      });

      chain.push({
        kind: 'branch',
        source: node,
        branches,
        convergeId,
      });

      currentId = convergeId;
      continue;
    }

    chain.push({ kind: 'step', node });

    if (outs.length === 0) break;
    currentId = outs[0].target;
  }

  return chain;
}

function humanLabelForHandle(handle, sourceType, sourceConfig) {
  if (sourceType === 'conditional') {
    if (handle === 'true') return 'If true';
    if (handle === 'false') return 'Otherwise';
  }
  if (sourceType === 'router') {
    if (handle === 'default') return 'Otherwise';
    const m = /^route-(\d+)$/.exec(handle || '');
    if (m && sourceConfig && Array.isArray(sourceConfig.rules)) {
      const idx = Number(m[1]);
      const rule = sourceConfig.rules[idx];
      if (rule && rule.label) return rule.label;
      return `Route ${idx + 1}`;
    }
  }
  if (handle) return handle;
  return 'Next';
}

/**
 * Public entry. Returns:
 *   {
 *     roots: [{ trigger: node|null, chain: LayoutNode[] }],
 *     orphanIds: string[],
 *   }
 *
 * `trigger` is the root node when it's a trigger-type; for malformed
 * workflows it may be a non-trigger root.
 */
export function buildSimpleLayout({ nodes, edges, nodeConfigs = {} }) {
  const cleanNodes = (nodes || []).filter((n) => n && n.id);
  const cleanEdges = (edges || []).filter(Boolean);

  const { nodesById, outBy, inDegree } = buildIndex(cleanNodes, cleanEdges);
  const roots = findRoots(nodesById, inDegree);
  const visited = new Set();

  const renderedRoots = roots.map((rootNode) => {
    const chain = walkChain({
      startId: rootNode.id,
      outBy,
      nodesById,
      nodeConfigs,
      visited,
      pathStack: new Set(),
    });
    return { trigger: rootNode, chain };
  });

  const orphanIds = [];
  for (const node of nodesById.values()) {
    if (!visited.has(node.id)) orphanIds.push(node.id);
  }

  return { roots: renderedRoots, orphanIds };
}
